import { createEvent, restore, sample } from 'effector'
import { fx } from '../../../libs'
import { Client, ManualTransfer } from './types'
import { Partner } from '../../ib/partners/details/types'
import { ClientWithdrawalStatusEdit } from '../edit/types'
import { $params } from '../../../features/router'
import {
  DividendBalance,
  dividendBalanceDefault,
} from '../../billing/deposits/edit/types'
import { getDividendBalance } from '../../billing/deposits/edit/model'
import { RequestError } from '../../../shared/errorResponse'

export const clientProfileMounted = createEvent()
export const partnerMounted = createEvent()
export const setTestUserMounted = createEvent()
export const toggleClientPremiumStatus = createEvent<{ status: boolean }>()
export const setClientProfile = createEvent<Client>()
export const setClientBan = createEvent<Client>()
export const setClientUnban = createEvent<Client>()
export const setClientDelete = createEvent()
export const setManualTransfer = createEvent<ManualTransfer>()

export const setManualTransferError = createEvent<RequestError>()

export const setClientProfileRequestError = createEvent<RequestError>()

export const setPartner = createEvent<{ id: string; firstName: string }>()
export const setFullPartnerForClient = createEvent<Partner | null>()

export const submitEditAutoWithdrawalStatus = createEvent<Client>()

export const submitManualTransfer = createEvent<ManualTransfer>()

export const dividendBalanceMounted = createEvent()

export const getPartnerFailed = createEvent<RequestError | null>()

export const $clientProfile = restore(setClientProfile, {
  clientId: '',
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  verified: false,
  isEmailConfirmed: false,
  country: '',
  fullName: '',
  tsname: '',
  hasDeposit: false,
  partner: false,
  banned: false,
  deleted: false,
  autoDepositDisabled: true,
  autoWithdrawalStatus: false,
  registeredAt: '',
  humanRegisteredAt: '',
  address: '',
  phone: '',
  dateOfBirth: '',
  socialNetwork: '',
  avatarUrl: '',
  gender: '',
  crown: '',
  verifications: [],
  products: [],
  language: '',
  isViewStrategies: false,
  isTest: false,
  isPremium: false,
  isManualPremium: false,
  premiumAssignedAt: '',
  humanPremiumAssignedAt: '',
  category: '',
  isTwoFaActive: false,
  twoFaType: '',
})

export const $manualTransfer = restore(setManualTransfer, {
  clientId: '',
  amount: '',
  commentForUser: '',
})

export const $errorManualTransfer = restore(setManualTransferError, {
  message: '',
  code: 0,
})

export const $errorClientProfileRequest = restore(
  setClientProfileRequestError,
  {
    message: '',
    code: 0,
  },
)

export const $partner = restore(setPartner, {
  id: '',
  firstName: '',
})

export const $fullPartnerForClient = restore<Partner | null>(
  setFullPartnerForClient,
  null,
).reset([clientProfileMounted])

export const $partnerError = restore<RequestError | null>(
  getPartnerFailed,
  null,
).reset([partnerMounted])

export const $dividendBalanceByClient = restore<DividendBalance>(
  getDividendBalance.doneData.map((a) => a.body),
  dividendBalanceDefault,
)

export const getClient = fx<void, Client>({
  method: 'GET',
  url: '/client/',
})

export const banClient = fx<Client, Client>({
  method: 'PUT',
  url: '/client/',
})

export const unbanClient = fx<Client, Client>({
  method: 'PUT',
  url: '/client/',
})

export const deleteClient = fx<Client, void>({
  method: 'DELETE',
  url: '/client/',
})

export const sendManualTransfer = fx<ManualTransfer, void>({
  method: 'POST',
  url: '/giftshop/manual_transfer',
})

export const getPartnerByClientId = fx<void, { id: string; firstName: string }>(
  {
    method: 'GET',
    url: '/client/{clientID}/partner',
  },
)

export const getFullPartnerForClient = fx<void, Partner>({
  method: 'GET',
  url: '/partner/',
})

export const setIsTest = fx<void, void>({
  method: 'PUT',
  url: '/client/{clientID}/set_is_test',
})

export const togglePremium = fx<{ status: boolean }, void>({
  method: 'POST',
  url: '/client/{clientID}/premium',
})

export const updateAutoWithdrawalStatus = fx<
  ClientWithdrawalStatusEdit,
  Client
>({
  method: 'POST',
  url: '/client/{id}/auto_withdrawal_status',
})

sample({
  clock: [
    clientProfileMounted,
    setIsTest.doneData,
    togglePremium.doneData,
    banClient.doneData,
    unbanClient.doneData,
  ],
  source: $params,
  fn: (params) => {
    return {
      url: `/client/${params?.clientID}`,
      query: {
        populate: JSON.stringify(['Client.verifications']),
      },
    }
  },
  target: getClient,
})

sample({
  clock: partnerMounted,
  source: $params,
  fn: (params) => {
    let id
    if (params) {
      id = params.clientID
    }
    return {
      url: `/client/${id}/partner`,
    }
  },
  target: getPartnerByClientId,
})

sample({
  clock: setClientBan,
  source: $params,
  fn: (params, client) => {
    let id
    if (params) {
      id = params.clientID
    }
    return {
      body: {
        ...client,
      },
      url: `/client/${id}`,
    }
  },
  target: banClient,
})

sample({
  clock: setClientUnban,
  source: $params,
  fn: (params, client) => {
    let id
    if (params) {
      id = params.clientID
    }
    return {
      body: {
        ...client,
      },
      url: `/client/${id}`,
    }
  },
  target: unbanClient,
})

sample({
  clock: setClientDelete,
  source: $params,
  fn: (params) => {
    let id
    if (params) {
      id = params.clientID
    }
    return {
      url: `/client/${id}`,
    }
  },
  target: deleteClient,
})

// update profile model

sample({
  clock: getClient.doneData,
  fn: (doneData) => doneData.body,
  target: setClientProfile,
})

sample({
  clock: getPartnerByClientId.doneData,
  fn: (doneData) => doneData.body,
  target: setPartner,
})

sample({
  clock: submitManualTransfer,
  fn: (manualTransfer) => {
    return {
      body: {
        ...manualTransfer,
      },
      url: `/giftshop/manual_transfer`,
    }
  },
  target: sendManualTransfer,
})

sample({
  clock: sendManualTransfer.failData,
  fn: (failData) => {
    return { message: failData.body.message, code: failData.status }
  },
  target: [setManualTransferError],
})

sample({
  clock: getClient.failData,
  fn: (failData) => {
    return { message: failData.body.message, code: failData.status }
  },
  target: [setClientProfileRequestError],
})

sample({
  clock: sendManualTransfer.doneData,
  fn: () => {
    return {
      clientId: '',
      amount: '',
      commentForUser: '',
    }
  },
  target: [setManualTransfer],
})

sample({
  clock: sendManualTransfer.doneData,
  fn: () => {
    return {
      clientId: '',
      amount: '',
      commentForUser: '',
    }
  },
  target: [setManualTransfer],
})

sample({
  clock: setTestUserMounted,
  source: $params,
  fn: (params) => {
    let id
    if (params) {
      id = params.clientID
    }
    return {
      url: `/client/${id}/set_is_test`,
    }
  },
  target: setIsTest,
})

sample({
  clock: dividendBalanceMounted,
  source: $params,
  fn: (params) => {
    let id
    if (params) {
      id = params.clientID
    }

    return {
      url: `/nova/dividend_balance/${id}`,
    }
  },
  target: getDividendBalance,
})

getPartnerByClientId.fail.watch(({ error }) =>
  getPartnerFailed({ code: error.status, message: error.body.message }),
)

getPartnerByClientId.done.watch(() => getPartnerFailed(null))

sample({
  clock: toggleClientPremiumStatus,
  source: $params,
  fn: (params, { status }) => {
    let id
    if (params) {
      id = params.clientID
    }
    return {
      url: `/client/${id}/premium`,
      body: {
        status,
      },
    }
  },
  target: togglePremium,
})

sample({
  clock: submitEditAutoWithdrawalStatus,
  source: $params,
  fn: (params, client) => {
    return {
      url: `/client/${params?.clientID}/auto_withdrawal_status`,
      body: { autoWithdrawalStatus: client.autoWithdrawalStatus },
    }
  },
  target: updateAutoWithdrawalStatus,
})

sample({
  clock: updateAutoWithdrawalStatus.doneData,
  source: $params,
  fn: (params) => {
    return {
      url: `/client/${params?.clientID}`,
      query: {
        populate: JSON.stringify(['Client.verifications']),
      },
    }
  },
  target: getClient,
})

sample({
  clock: setClientProfile,
  filter: (client) => !!client.partner,
  fn: (client) => ({
    url: `/partner/${client.clientId}`,
    query: {
      populate: JSON.stringify([
        'Partner.sumCommissionsLevelTwo',
        'Partner.sumCommissionsLevelThree',
      ]),
    },
  }),
  target: getFullPartnerForClient,
})

sample({
  clock: getFullPartnerForClient.doneData,
  fn: (doneData) => doneData.body,
  target: setFullPartnerForClient,
})
